import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePoints } from '@/hooks/usePoints';
import {
  getPurchasedAttachments,
  purchaseAttachmentByPoints,
  recordDownload,
  type AttachmentResourceType,
  type AttachmentType,
} from '@/api/interaction';

export type AttachmentDownloadType = 'free' | 'timed_free' | 'lingshi' | 'vip';

export interface AttachmentWithPermission {
  id?: string | number;
  name: string;
  size: string;
  url: string;
  downloadUrl?: string | null;
  downloadType?: AttachmentDownloadType;
  downloadTypes?: AttachmentDownloadType[];
  lingshiCost?: number;
  timedFreeUntil?: string;
  vipPrivilege?: boolean;
}

function resolveAttachmentType(resourceType: AttachmentResourceType): AttachmentType {
  return resourceType === 'solution' ? 'solution_attachment' : 'tool_attachment';
}

function isAdmin(user: ReturnType<typeof useAuth>['user']): boolean {
  if (!user) return false;
  const role = (user as { role?: string }).role;
  const roles = Array.isArray((user as { roles?: string[] }).roles)
    ? ((user as { roles?: string[] }).roles as string[])
    : [];
  return role === 'admin' || role === 'super_admin' || roles.includes('admin') || roles.includes('super_admin');
}

function isVipUser(user: ReturnType<typeof useAuth>['user']): boolean {
  if (!user) return false;
  if (isAdmin(user)) return true;
  const membershipTier = (user as { membershipTier?: string }).membershipTier;
  const role = (user as { role?: string }).role;
  return membershipTier === 'vip' || role === 'vip';
}

function resolveLingshiCost(attachment: AttachmentWithPermission): number | null {
  if (typeof attachment.lingshiCost !== 'number' || !Number.isFinite(attachment.lingshiCost)) {
    return null;
  }
  const normalized = Math.trunc(attachment.lingshiCost);
  return normalized >= 0 ? normalized : null;
}

function toPositiveInt(value: unknown): number | null {
  const num = Number(value);
  if (!Number.isInteger(num) || num <= 0) return null;
  return num;
}

export function useAttachmentDownload(resourceType: AttachmentResourceType, resourceId: string) {
  const { isLoggedIn, user } = useAuth();
  const { refresh: refreshPoints } = usePoints();
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const [purchasedIds, setPurchasedIds] = useState<Set<string>>(new Set());

  const safeResourceId = useMemo(() => {
    const parsed = Number(resourceId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [resourceId]);

  const attachmentType = useMemo(() => resolveAttachmentType(resourceType), [resourceType]);

  const showToast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const syncPurchasedIds = useCallback(async () => {
    if (!isLoggedIn || !safeResourceId) {
      setPurchasedIds(new Set());
      return;
    }
    try {
      const res = await getPurchasedAttachments({
        attachmentType,
        resourceType,
        resourceId: safeResourceId,
      });
      setPurchasedIds(new Set((res.attachmentIds ?? []).map((id) => String(id))));
    } catch (err) {
      console.error('Failed to sync purchased attachments:', err);
      setPurchasedIds(new Set());
    }
  }, [attachmentType, isLoggedIn, resourceType, safeResourceId]);

  useEffect(() => {
    void syncPurchasedIds();
  }, [syncPurchasedIds]);

  const getEffectiveTypes = useCallback((attachment: AttachmentWithPermission): AttachmentDownloadType[] => {
    const validTypes: AttachmentDownloadType[] = ['free', 'timed_free', 'lingshi', 'vip'];
    if (Array.isArray(attachment.downloadTypes) && attachment.downloadTypes.length > 0) {
      return attachment.downloadTypes.filter((item): item is AttachmentDownloadType =>
        validTypes.includes(item as AttachmentDownloadType),
      );
    }
    if (attachment.downloadType && validTypes.includes(attachment.downloadType)) {
      return [attachment.downloadType];
    }
    return [];
  }, []);

  const isPurchased = useCallback((attachment: AttachmentWithPermission) => {
    const id = attachment.id;
    if (id === undefined || id === null) return false;
    return purchasedIds.has(String(id));
  }, [purchasedIds]);

  const checkAndDownload = useCallback(async (
    attachment: AttachmentWithPermission,
    _attachIndex: number,
    onSuccess: (url: string) => void,
    onRequireLogin: () => void,
  ): Promise<void> => {
    const resolvedDownloadUrl = String(attachment.downloadUrl || attachment.url || '').trim();
    const wrappedOnSuccess = (url: string) => {
      if (isLoggedIn && safeResourceId) {
        const fileObjId = (attachment as any).fileObjectId;
        void recordDownload({
          targetType: resourceType,
          targetId: safeResourceId,
          toolId: resourceType === 'tool' ? safeResourceId : undefined,
          fileObjectId: fileObjId ? Number(fileObjId) : undefined,
          sourceType: resourceType === 'tool' ? 'tool_attachment' : 'solution_attachment',
          versionSnapshot: attachment.name,
          downloadUrlSnapshot: url,
        });
      }
      onSuccess(url);
    };

    const types = getEffectiveTypes(attachment);
    const hasVipPrivilege = types.includes('vip') || attachment.vipPrivilege;
    if (types.length === 0) {
      showToast('附件下载权限未配置，暂不可下载', 'error');
      return;
    }

    if (isLoggedIn && isAdmin(user)) {
      wrappedOnSuccess(resolvedDownloadUrl);
      return;
    }

    if (hasVipPrivilege && isLoggedIn && isVipUser(user)) {
      wrappedOnSuccess(resolvedDownloadUrl);
      return;
    }

    const primaryTypes = types.filter((t) => t !== 'vip');
    const primaryType = primaryTypes.length > 0 ? primaryTypes[0] : (hasVipPrivilege ? 'vip' : null);
    if (!primaryType) {
      showToast('附件下载权限未配置，暂不可下载', 'error');
      return;
    }

    if (primaryType === 'free') {
      wrappedOnSuccess(resolvedDownloadUrl);
      return;
    }

    if (primaryType === 'timed_free') {
      const until = attachment.timedFreeUntil;
      if (!until) {
        showToast('附件限免截止时间未配置，暂不可下载', 'error');
        return;
      }
      const now = new Date();
      const freeUntil = new Date(until);
      if (Number.isNaN(freeUntil.getTime())) {
        showToast('附件限免截止时间格式错误，暂不可下载', 'error');
        return;
      }
      if (now <= freeUntil) {
        wrappedOnSuccess(resolvedDownloadUrl);
        return;
      }
      showToast('限时免费已结束，需消耗灵石下载', 'info');
    }

    if (primaryType === 'lingshi' || primaryType === 'timed_free') {
      if (!isLoggedIn) {
        setShowLoginHint(true);
        onRequireLogin();
        return;
      }
      if (!safeResourceId) {
        showToast('资源 ID 无效，暂不可下载', 'error');
        return;
      }
      const attachmentId = toPositiveInt(attachment.id);
      if (!attachmentId) {
        showToast('附件 ID 缺失，暂不可下载', 'error');
        return;
      }

      if (isPurchased(attachment)) {
        wrappedOnSuccess(resolvedDownloadUrl);
        return;
      }

      const cost = resolveLingshiCost(attachment);
      if (cost === null) {
        showToast('附件灵石价格未配置，暂不可下载', 'error');
        return;
      }

      if (cost > 0) {
        const shouldPurchase = window.confirm(`该附件需要消耗 ${cost} 灵石，是否确认购买并下载？`);
        if (!shouldPurchase) {
          return;
        }
      }

      try {
        const res = await purchaseAttachmentByPoints({
          attachmentType,
          attachmentId,
          resourceType,
          resourceId: safeResourceId,
        });
        setPurchasedIds((prev) => new Set(prev).add(String(attachmentId)));
        window.dispatchEvent(new CustomEvent('wanxiang_points_updated'));
        void refreshPoints();
        showToast(res.message, 'success');
        // 由于后端购买附件接口不再主动向历史下载日志表 (download_records) 写入数据，
        // 我们在此处触发真正的下载回调以在客户端点击时进行可靠的下载日志埋点上报。
        wrappedOnSuccess(resolvedDownloadUrl);
      } catch (err) {
        console.error('Failed to purchase attachment:', err);
        showToast(err instanceof Error ? err.message : '附件下载失败，请稍后重试', 'error');
      }
      return;
    }

    if (primaryType === 'vip') {
      if (!isLoggedIn) {
        setShowLoginHint(true);
        onRequireLogin();
        return;
      }
      if (isVipUser(user)) {
        wrappedOnSuccess(resolvedDownloadUrl);
        return;
      }
      showToast('此附件为 VIP 专属，请升级会员后下载', 'error');
      return;
    }

    wrappedOnSuccess(resolvedDownloadUrl);
  }, [attachmentType, getEffectiveTypes, isLoggedIn, isPurchased, refreshPoints, resourceType, safeResourceId, showToast, user]);

  const getAttachmentStatus = useCallback((
    attachment: AttachmentWithPermission,
    _attachIndex: number,
  ): {
    label: string;
    extraLabel?: string;
    color: string;
    icon: string;
    locked: boolean;
    cost?: number;
    hasVipPrivilege?: boolean;
  } => {
    const types = getEffectiveTypes(attachment);
    const hasVipPrivilege = types.includes('vip') || !!attachment.vipPrivilege;

    if (types.length === 0) {
      return { label: '未配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true };
    }

    if (isLoggedIn && isAdmin(user)) {
      return { label: '管理员免费', color: '#8b5cf6', icon: 'ri-shield-user-line', locked: false, hasVipPrivilege };
    }

    if (hasVipPrivilege && isLoggedIn && isVipUser(user)) {
      return { label: 'VIP免费', color: '#f59e0b', icon: 'ri-vip-crown-line', locked: false, hasVipPrivilege: true };
    }

    const primaryTypes = types.filter((t) => t !== 'vip');
    const primaryType = primaryTypes.length > 0 ? primaryTypes[0] : (hasVipPrivilege ? 'vip' : null);
    if (!primaryType) {
      return { label: '未配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true };
    }

    const vipExtra = hasVipPrivilege ? 'VIP可免费' : undefined;

    if (primaryType === 'free') {
      return { label: '免费', color: '#10b981', icon: 'ri-download-line', locked: false, extraLabel: vipExtra, hasVipPrivilege };
    }

    if (primaryType === 'timed_free') {
      const until = attachment.timedFreeUntil;
      if (!until) {
        return { label: '待配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true, extraLabel: vipExtra, hasVipPrivilege };
      }
      const now = new Date();
      const freeUntil = new Date(until);
      if (Number.isNaN(freeUntil.getTime())) {
        return { label: '待配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true, extraLabel: vipExtra, hasVipPrivilege };
      }
      if (now <= freeUntil) {
        const days = Math.ceil((freeUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return { label: `限免 ${days}天`, color: '#f97316', icon: 'ri-time-line', locked: false, extraLabel: vipExtra, hasVipPrivilege };
      }
      const cost = resolveLingshiCost(attachment);
      if (cost === null) {
        return { label: '待配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true, extraLabel: vipExtra, hasVipPrivilege };
      }
      if (isPurchased(attachment)) {
        return { label: '已购', color: '#10b981', icon: 'ri-checkbox-circle-line', locked: false, cost, extraLabel: vipExtra, hasVipPrivilege };
      }
      return { label: `${cost} 灵石`, color: '#f59e0b', icon: 'ri-copper-coin-line', locked: true, cost, extraLabel: vipExtra, hasVipPrivilege };
    }

    if (primaryType === 'lingshi') {
      const cost = resolveLingshiCost(attachment);
      if (cost === null) {
        return { label: '待配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true, extraLabel: vipExtra, hasVipPrivilege };
      }
      if (isPurchased(attachment)) {
        return { label: '已购', color: '#10b981', icon: 'ri-checkbox-circle-line', locked: false, cost, extraLabel: vipExtra, hasVipPrivilege };
      }
      return { label: `${cost} 灵石`, color: '#f59e0b', icon: 'ri-copper-coin-line', locked: true, cost, extraLabel: vipExtra, hasVipPrivilege };
    }

    if (primaryType === 'vip') {
      if (isLoggedIn && isVipUser(user)) {
        return { label: 'VIP', color: '#f59e0b', icon: 'ri-vip-crown-line', locked: false, hasVipPrivilege: true };
      }
      return { label: 'VIP专属', color: '#f59e0b', icon: 'ri-vip-crown-line', locked: true, hasVipPrivilege: true };
    }

    return { label: '未配置', color: '#ef4444', icon: 'ri-error-warning-line', locked: true };
  }, [getEffectiveTypes, isLoggedIn, isPurchased, user]);

  const dismissLoginHint = useCallback(() => setShowLoginHint(false), []);

  return { checkAndDownload, getAttachmentStatus, toast, showLoginHint, dismissLoginHint, refreshPurchased: syncPurchasedIds };
}
